import { createContext, ReactNode, useCallback, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { StatusLabel } from '@shared/components';
import config from '@config/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { RetryLogin } from '@shared/pages';
import { Logout } from '@modules/users';
import AiCredits from '@modules/aiCredits/AiCredits.page';

type TProps = {
  children: ReactNode;
};
type TAuthContext = {
  getAccessToken: () => Promise<string>;
  logout: () => void;
  account: AccountInfo;
};

// @ts-ignore
const AuthContext = createContext<TAuthContext>();
export default AuthContext;

export const AuthProvider = ({ children }: TProps) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const location = useLocation();
  const navigate = useNavigate();

  const getAccessToken = useCallback(async () => {
    let res;
    const options = {
      scopes: [`api://${config.MS_CLIENT_ID}/access_as_user`], // this scope requests the permissions that are statically configured on the Azure AD app registration
      account: accounts[0],
    };

    try {
      res = await instance.acquireTokenSilent(options);
    } catch (e) {
      // if (e instanceof BrowserAuthError && e.errorCode === 'monitor_window_timeout')
      // if (error instanceof InteractionRequiredAuthError)
      await instance.acquireTokenRedirect(options);
      res = await instance.acquireTokenSilent(options);

      // We shouldn't redirect here, as the internet connection might be down and the user might have unsaved content
    }
    return res.accessToken;
  }, [instance, accounts]);

  const logout = useCallback(() => {
    instance.logout({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      inProgress === InteractionStatus.None &&
      location.pathname !== '/retry' &&
      location.pathname !== '/logout' &&
      location.pathname !== '/ai-credits'
    ) {
      instance
        .loginRedirect({
          scopes: [`api://${config.MS_CLIENT_ID}/access_as_user`], // this scope requests the permissions that are statically configured on the Azure AD app registration
        })
        .catch(() => {
          sessionStorage.clear();
          localStorage.clear();

          instance
            .loginRedirect({
              scopes: [`api://${config.MS_CLIENT_ID}/access_as_user`],
            })
            .catch((e) => {
              console.error(e); // TODO: log
              navigate('/retry');
            });
        });
    }
  }, [location.pathname, isAuthenticated, inProgress, instance]);

  if (location.pathname === '/retry') return <RetryLogin />;

  if (location.pathname === '/logout') return <Logout logout={logout} />;

  if (location.pathname === '/ai-credits') return <AiCredits />;

  const loggedInd = inProgress === InteractionStatus.None && accounts.length >= 1;

  return (
    <StatusLabel data={loggedInd} loadingMessage="Authenticating...">
      <AuthContext.Provider
        value={{
          getAccessToken,
          logout,
          account: accounts[0],
        }}
      >
        {children}
      </AuthContext.Provider>
    </StatusLabel>
  );
};
